import {testIdProps} from '@github-ui/test-id-props'
import {AlertFillIcon, CheckIcon, XIcon} from '@primer/octicons-react'
import {Button, Link, Spinner} from '@primer/react'
import {Octicon} from '@primer/react/deprecated'

import type {ProjectMigration} from '../api/project-migration/contracts'
import {ProjectMigrationResources} from '../strings'
import styles from './project-migration-overlay-description.module.css'

interface MigrationOverlayDescriptionProps {
  migration: ProjectMigration
}

interface MigrationOverlayCompletedProps extends MigrationOverlayDescriptionProps {
  closeOverlay?: () => void
}

interface MigrationOverlayErrorProps extends MigrationOverlayDescriptionProps {
  cancelMigration?: () => Promise<void>
  tryAgain?: () => Promise<void>
}

type MigrationOverlayDescriptionType = MigrationOverlayDescriptionProps &
  MigrationOverlayCompletedProps &
  MigrationOverlayErrorProps

const getStatusMessageFromMigration = (migration: ProjectMigration): string => {
  if (migration.status === 'completion_acknowledged' || migration.status === 'completed') {
    return ''
  }

  return ProjectMigrationResources.statuses[migration.status]
}

const MigrationOverlayCompleted: React.FC<MigrationOverlayCompletedProps> = ({migration, closeOverlay}) => {
  return (
    <div>
      <div className={styles.Box}>
        <div className={styles.Box_1}>
          {migration.source_project?.closed ? (
            <Octicon icon={CheckIcon} className={styles.Octicon} />
          ) : (
            <Octicon icon={AlertFillIcon} className={styles.Octicon_1} />
          )}
        </div>

        <div className={styles.Box_2}>
          <span {...testIdProps('migration-status')}>
            {migration.source_project?.closed ? (
              <>
                {'• '}
                <Link target="_blank" rel="noreferrer" href={migration.source_project?.path}>
                  {ProjectMigrationResources.yourClassicProject}
                </Link>
                {` ${ProjectMigrationResources.completedAndClosed1}`}
                <br />
                {`• ${ProjectMigrationResources.completedAndClosed2}`}
              </>
            ) : (
              <>
                <Link target="_blank" rel="noreferrer" href={migration.source_project?.path}>
                  {ProjectMigrationResources.yourClassicProject}
                </Link>
                {` ${ProjectMigrationResources.completedAndStillOpen}`}
              </>
            )}
          </span>
        </div>
      </div>
      <div className={styles.Box_3}>
        <Button variant="primary" onClick={closeOverlay}>
          Done
        </Button>
      </div>
    </div>
  )
}

const MigrationOverlayError: React.FC<MigrationOverlayErrorProps> = ({migration, cancelMigration, tryAgain}) => {
  return (
    <div>
      <div className={styles.Box}>
        <div className={styles.Box_1}>
          <Octicon icon={XIcon} className={styles.Octicon_2} />
        </div>

        <div {...testIdProps('migration-status')} className={styles.Box_2}>
          {getStatusMessageFromMigration(migration)}
        </div>
      </div>
      <div className={styles.Box_4}>
        <Button variant="primary" onClick={tryAgain} className={styles.Button}>
          Try again
        </Button>
        <Button onClick={cancelMigration}>Cancel</Button>
      </div>
    </div>
  )
}

export const MigrationOverlayDescription: React.FC<MigrationOverlayDescriptionType> = ({
  migration,
  closeOverlay,
  ...errorProps
}) => {
  if (migration.status === 'completed') {
    return <MigrationOverlayCompleted migration={migration} closeOverlay={closeOverlay} />
  }

  if (migration.status === 'error') {
    return <MigrationOverlayError migration={migration} {...errorProps} />
  }

  return (
    <div className={styles.Box}>
      <div className={styles.Box_5}>
        <Spinner size="small" />
      </div>
      <div {...testIdProps('migration-status')} className={styles.Box_6}>
        {getStatusMessageFromMigration(migration)}
      </div>
    </div>
  )
}

try{ MigrationOverlayCompleted.displayName ||= 'MigrationOverlayCompleted' } catch {}
try{ MigrationOverlayError.displayName ||= 'MigrationOverlayError' } catch {}
try{ MigrationOverlayDescription.displayName ||= 'MigrationOverlayDescription' } catch {}